import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import RefreshButton from '../components/common/RefreshButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getCashShifts, refreshCash, refreshData } from '../services/api';
import { formatMoney, formatNumber, formatDateTime } from '../utils/formatters';
import '../components/common/ui.css';
import './CashPage.css';

/* Иконки вместо эмодзи (см. Navbar.jsx). */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconClock = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M12 8V12L14.5 14.5" /></Icon>;
const IconUnlocked = () => <Icon><path d="M2 16C2 13.1716 2 11.7574 2.87868 10.8787C3.75736 10 5.17157 10 8 10H16C18.8284 10 20.2426 10 21.1213 10.8787C22 11.7574 22 13.1716 22 16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H8C5.17157 22 3.75736 22 2.87868 21.1213C2 20.2426 2 18.8284 2 16Z" /><path d="M6 10V8C6 4.68629 8.68629 2 12 2C14.7958 2 17.1449 3.91216 17.811 6.5" /></Icon>;
const IconLocked = () => <Icon><path d="M2 16C2 13.1716 2 11.7574 2.87868 10.8787C3.75736 10 5.17157 10 8 10H16C18.8284 10 20.2426 10 21.1213 10.8787C22 11.7574 22 13.1716 22 16C22 18.8284 22 20.2426 21.1213 21.1213C20.2426 22 18.8284 22 16 22H8C5.17157 22 3.75736 22 2.87868 21.1213C2 20.2426 2 18.8284 2 16Z" /><path d="M6 10V8C6 4.68629 8.68629 2 12 2C15.3137 2 18 4.68629 18 8V10" /></Icon>;
const IconWallet = () => <Icon><path d="M6 10H10" /><path d="M20.8333 11H18.2308C16.4465 11 15 12.3431 15 14C15 15.6569 16.4465 17 18.2308 17H20.8333C20.9167 17 20.9583 17 20.9935 16.9979C21.5328 16.965 21.9623 16.5662 21.9977 16.0654C22 16.0327 22 15.994 22 15.9167V12.0833C22 12.006 22 11.9673 21.9977 11.9346C21.9623 11.4338 21.5328 11.035 20.9935 11.0021C20.9583 11 20.9167 11 20.8333 11Z" /><path d="M20.965 11C20.8873 9.1277 20.6366 7.97975 19.8284 7.17157C18.6569 6 16.7712 6 13 6H10C6.22876 6 4.34315 6 3.17157 7.17157C2 8.34315 2 10.2288 2 14C2 17.7712 2 19.6569 3.17157 20.8284C4.34315 22 6.22876 22 10 22H13C16.7712 22 18.6569 22 19.8284 20.8284C20.6366 20.0203 20.8873 18.8723 20.965 17" /><path d="M6 6L9.73549 3.52313C10.7874 2.82562 12.2126 2.82562 13.2645 3.52313L17 6" /><path d="M17.9912 14H18.0002" /></Icon>;
const IconBanknote = () => <Icon><path d="M9 19C6.19108 19 4.78661 19 3.77772 18.3259C3.34096 18.034 2.96596 17.659 2.67412 17.2223C2 16.2134 2 14.8089 2 12C2 9.19108 2 7.78661 2.67412 6.77772C2.96596 6.34096 3.34096 5.96596 3.77772 5.67412C4.78661 5 6.19108 5 9 5L15 5C17.8089 5 19.2134 5 20.2223 5.67412C20.659 5.96596 21.034 6.34096 21.3259 6.77772C22 7.78661 22 9.19108 22 12C22 14.8089 22 16.2134 21.3259 17.2223C21.034 17.659 20.659 18.034 20.2223 18.3259C19.2134 19 17.8089 19 15 19H9Z" /><path d="M12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9Z" /><path d="M5.5 15L5.5 9" /><path d="M18.5 15L18.5 9" /></Icon>;
const IconCard = () => <Icon><path d="M2 12C2 8.22876 2 6.34315 3.17157 5.17157C4.34315 4 6.22876 4 10 4H14C17.7712 4 19.6569 4 20.8284 5.17157C22 6.34315 22 8.22876 22 12C22 15.7712 22 17.6569 20.8284 18.8284C19.6569 20 17.7712 20 14 20H10C6.22876 20 4.34315 20 3.17157 18.8284C2 17.6569 2 15.7712 2 12Z" /><path d="M10 16H6" /><path d="M14 16H12.5" /><path d="M2 10L22 10" /></Icon>;

const CashPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cashData, setCashData] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getCashShifts();
      setCashData(data);
      setError('');
    } catch (e) {
      // Пустой список смен неотличим от «касс не открывали», поэтому ошибку
      // показываем явно, а не оставляем страницу с нулями.
      setError(e.response?.data?.error || 'Не удалось загрузить кассовые смены');
      setCashData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshCash();
      await refreshData();
      await loadData();
    } finally {
      setRefreshing(false);
    }
  };

  if (!isAuthenticated) return null;

  const openShifts = cashData?.open_shifts || [];
  const closedShifts = cashData?.closed_shifts || [];
  const total = cashData?.total || {};

  return (
    <div className="cash-container">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />
      
      <div className="cash-header">
        <div className="date-badge">
          <span><IconClock /></span>
          <span>Обновлено: {cashData?.last_updated || '—'}</span>
        </div>
        <RefreshButton onRefresh={handleRefresh} loading={refreshing} />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="ui-inline-error cash-error">
          <span>{error}</span>
          <button type="button" className="btn-primary" onClick={handleRefresh} disabled={refreshing}>
            Повторить
          </button>
        </div>
      ) : (
        <>
          {/* Статистика */}
          <div className="cash-stats">
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon purple"><IconUnlocked /></div>
                <div className="stat-title">Открытых смен</div>
              </div>
              <div className="stat-value">{formatNumber(total.open_count || 0)}</div>
              <div className="stat-label">активных касс</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon green"><IconWallet /></div>
                <div className="stat-title">Выручка в сменах</div>
              </div>
              <div className="stat-value">{formatMoney(total.total_revenue)}</div>
              <div className="stat-label">открытые смены</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon blue"><IconBanknote /></div>
                <div className="stat-title">Наличные</div>
              </div>
              <div className="stat-value">{formatMoney(total.total_cash)}</div>
              <div className="stat-label">в открытых сменах</div>
            </div>

            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon orange"><IconCard /></div>
                <div className="stat-title">Безналичные</div>
              </div>
              <div className="stat-value">{formatMoney(total.total_non_cash)}</div>
              <div className="stat-label">в открытых сменах</div>
            </div>
          </div>

          {/* Открытые смены */}
          <div className="panel">
            <h2>
              <IconUnlocked /> Открытые кассовые смены
              <span className="badge badge-open">{openShifts.length}</span>
            </h2>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Касса</th>
                    <th>Сотрудник</th>
                    <th>Открыта</th>
                    <th>Выручка</th>
                    <th>Наличные</th>
                    <th>Безналичные</th>
                    <th>Внесения</th>
                    <th>Изъятия</th>
                    <th>Остаток</th>
                  </tr>
                </thead>
                <tbody>
                  {openShifts.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="empty-cell">Нет открытых смен</td>
                    </tr>
                  ) : (
                    openShifts.map((shift, index) => (
                      <tr key={shift.id || index} className="open-row">
                        <td><strong>{shift.cash_reg_number || '—'}</strong></td>
                        <td>{shift.responsible_user || '—'}</td>
                        <td>{formatDateTime(shift.open_date)}</td>
                        <td><strong>{formatMoney(shift.pay_orders)}</strong></td>
                        <td>{formatMoney(shift.sales_cash)}</td>
                        <td>{formatMoney(shift.sales_card)}</td>
                        <td>{formatMoney(shift.pay_in)}</td>
                        <td>{formatMoney(shift.pay_out)}</td>
                        <td>{formatMoney(shift.session_start_cash)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Закрытые смены */}
          <div className="panel">
            <h2><IconLocked /> Последние закрытые смены</h2>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Касса</th>
                    <th>Сотрудник</th>
                    <th>Открыта</th>
                    <th>Закрыта</th>
                    <th>Выручка</th>
                    <th>Наличные</th>
                    <th>Безналичные</th>
                    <th>Внесения</th>
                    <th>Изъятия</th>
                    <th>Остаток</th>
                  </tr>
                </thead>
                <tbody>
                  {closedShifts.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="empty-cell">Нет закрытых смен</td>
                    </tr>
                  ) : (
                    closedShifts.map((shift, index) => (
                      <tr key={shift.id || index}>
                        <td><strong>{shift.cash_reg_number || '—'}</strong></td>
                        <td>{shift.responsible_user || '—'}</td>
                        <td>{formatDateTime(shift.open_date)}</td>
                        <td>{formatDateTime(shift.close_date)}</td>
                        <td><strong>{formatMoney(shift.pay_orders)}</strong></td>
                        <td>{formatMoney(shift.sales_cash)}</td>
                        <td>{formatMoney(shift.sales_card)}</td>
                        <td>{formatMoney(shift.pay_in)}</td>
                        <td>{formatMoney(shift.pay_out)}</td>
                        <td>{formatMoney(shift.session_start_cash)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CashPage;